import type { Ref } from "vue";
import { HttpClient } from "@/services/httpClient";
import { FrappeMethodType } from "@/type/api";
import { dedupeInFlight } from "@/services/frappe/dedupe";

export interface FrappeListResult<T> {
  data: T[];
  total: number;
}

export type FrappeListResponse<T> =
  | T[]
  | {
      data?: T[] | null;
      total?: number | null;
    }
  | null
  | undefined;

export async function withFrappeLoading<T>(
  loading: Ref<boolean>,
  task: () => Promise<T>,
): Promise<T> {
  loading.value = true;

  try {
    return await task();
  } finally {
    loading.value = false;
  }
}

export function normalizeFrappeListData<T>(
  response: FrappeListResponse<T>,
): T[] {
  if (Array.isArray(response))
    return response;

  return response?.data ?? [];
}

export function normalizeFrappeListResult<T>(
  response: FrappeListResponse<T>,
  total?: number | null,
): FrappeListResult<T> {
  const data = normalizeFrappeListData(response);
  const responseTotal =
    response && !Array.isArray(response) ? response.total : undefined;

  return {
    data,
    total:
      typeof total === "number"
        ? total
        : typeof responseTotal === "number"
          ? responseTotal
          : data.length,
  };
}

export async function fetchFrappeDocCount(
  service: HttpClient,
  doctype: string,
  filters?: string,
  orFilters?: string,
): Promise<number> {
  // Deduped: a count is a plain number, so concurrent callers sharing one
  // response carries no aliasing risk whatsoever.
  const response = await dedupeInFlight(
    `get_count|${doctype}|${filters ?? ""}|${orFilters ?? ""}`,
    () => service.get<{
      message?: number;
      data?: number;
      count?: number;
    }>(
      "frappe.client.get_count",
      {},
      {
        doctype,
        filters,
        or_filters: orFilters,
      },
      undefined,
      undefined,
      FrappeMethodType.method,
    ),
  );

  const count =
    typeof response?.message === "number"
      ? response.message
      : typeof response?.data === "number"
        ? response.data
        : typeof response?.count === "number"
          ? response.count
          : 0;

  return Number.isFinite(count) ? count : 0;
}
